import moment from "moment";
import {useNavigate} from "react-router-dom";

import Page from "@/components/Page";
import {
	DsHeader,
	DsKpi,
	DsKpiRow,
	DsLink,
	DsMissingSources,
	DsSection,
	DsStatus,
	DsTable,
	DsSkeleton,
} from "@/components/DsKit";

import {useClubCertificates, useClubSnapshot} from "@/utils/hooks";
import {
	playerName,
	dsMembershipLabel,
	summarizeDsMedicalDays,
} from "@/utils/clubSnapshot";

function DsMedicalPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {
		data: s,
		loading,
		reload,
		scope,
	} = useClubSnapshot({handleNotification});
	const {data: certificates, loading: certificatesLoading} =
		useClubCertificates({snapshot: s});

	const medicalDays = summarizeDsMedicalDays(s);
	const injuries = s?.activeInjuries || [];
	const episodes = s?.activeInjuryEpisodes || [];
	const returning = episodes.filter(
		(i) =>
			i.daysToDischarge !== null &&
			i.daysToDischarge > 0 &&
			i.daysToDischarge <= 14,
	);
	const certStats =
		certificates?.tracked && !certificates.failed
			? [...certificates.byPlayer.values()].reduce((acc, c) => {
					acc[c.status] = (acc[c.status] || 0) + 1;
					return acc;
				}, {})
			: null;

	const injuryColumns = [
		{
			key: "player",
			label: "Giocatore",
			render: (i) => (
				<>
					<div className="ds-strong">{playerName(i.player)}</div>
					<div className="ds-muted">{dsMembershipLabel(i)}</div>
				</>
			),
		},
		{
			key: "type",
			label: "Problema",
			render: (i) => i.diagnosis || i.injuryType || "—",
		},
		{
			key: "since",
			label: "Inizio",
			align: "center",
			render: (i) => moment(i.startDate).format("DD/MM/YYYY"),
		},
		{
			key: "end",
			label: "Fine",
			render: (i) =>
				i.endDate ? moment(i.endDate).format("DD/MM/YYYY") : "Non indicata",
		},
		{
			key: "medicalDischarge",
			label: "Dimissione medica",
			render: (i) =>
				i.medicalDischargeDate
					? moment(i.medicalDischargeDate).format("DD/MM/YYYY")
					: "Non indicata",
		},
		{
			key: "sportsDischarge",
			label: "Dimissione sportiva",
			render: (i) =>
				i.sportsDischargeDate
					? moment(i.sportsDischargeDate).format("DD/MM/YYYY")
					: "Non indicata",
		},
	];

	const teamRows = (s?.teams || []).map((t) => ({
		id: t.id,
		name: t.name,
		injured: t.injured.length,
		players: t.players.length,
	}));

	return (
		<Page>
			<div className="ds">
				<DsHeader
					title="Area medica"
					subtitle="Disponibilità, rientri e idoneità"
					snapshot={s}
					scope={scope}
					area="medica"
					onReload={reload}
					reloading={loading}
				/>
				<DsSkeleton visible={loading && !s} />
				{s && (
					<>
						<DsMissingSources sources={s.missingSources} />
						<DsKpiRow>
							<DsKpi
								label="Infortunati"
								value={injuries.length}
								tone={injuries.length ? "red" : "green"}
								sub={`su ${s.totalPlayers} giocatori`}
							/>
							<DsKpi
								label="Dimissioni inserite"
								value={returning.length}
								sub="episodi con data sportiva nei prossimi 14 giorni"
							/>
							<DsKpi
								label="Disponibilità stagionale"
								value={
									medicalDays.availability === null
										? "—"
										: medicalDays.availability.toFixed(1) + "%"
								}
								sub={`${medicalDays.known}/${medicalDays.total} cartelle · giorni senza infortunio / ${medicalDays.elapsed} giorni trascorsi · sovrapposizioni unite`}
							/>
							<DsKpi label="Episodi attivi" value={episodes.length} />
							<DsKpi
								label="Giorni persi"
								value={medicalDays.days ?? "—"}
								sub={`${medicalDays.known}/${medicalDays.total} cartelle · giorni-persona, stagione fino a oggi`}
							/>
							<DsKpi
								label="Idoneità da regolarizzare"
								value={
									certStats
										? (certStats.expired || 0) + (certStats.missing || 0)
										: certificatesLoading && !certificates
											? "…"
											: "—"
								}
								tone={
									certStats && (certStats.expired || certStats.missing)
										? "red"
										: undefined
								}
								sub={
									certStats
										? `${certStats.expiring || 0} in scadenza entro 30 gg`
										: certificates?.failed
											? "certificati non disponibili"
											: certificates && !certificates.tracked
												? "certificati non ancora inseriti"
												: "certificati medico-sportivi"
								}
							/>
						</DsKpiRow>

						<DsSection
							title="Infortunati attuali"
							hint={
								"Un giocatore può avere più episodi attivi. " +
								(s.medicalPlayerIds?.size ?? 0) +
								"/" +
								s.totalPlayers +
								" giocatori con cartella consultata; nessuna cartella non equivale a idoneità."
							}
							action={
								<DsLink to="/medical/overview">Area medica completa</DsLink>
							}
						>
							<DsTable
								columns={injuryColumns}
								rows={episodes}
								onRowClick={(i) =>
									navigate(`/medical/records/${i.recordId}/injuries/${i.id}`)
								}
								empty="Nessun infortunio attivo nelle cartelle consultate."
							/>
						</DsSection>

						<DsSection title="Disponibilità per squadra">
							<DsTable
								columns={[
									{
										key: "name",
										label: "Squadra",
										render: (r) => <b>{r.name}</b>,
									},
									{
										key: "injured",
										label: "Infortunati",
										align: "center",
										render: (r) => (
											<DsStatus
												status={
													!r.injured
														? "ok"
														: r.players && r.injured / r.players > 0.15
															? "alert"
															: "warn"
												}
												label={r.injured}
											/>
										),
									},
									{
										key: "available",
										label: "Senza infortunio attivo in cartella",
										align: "center",
										render: (r) => `${r.players - r.injured}/${r.players}`,
									},
								]}
								rows={teamRows}
							/>
						</DsSection>
					</>
				)}
			</div>
		</Page>
	);
}

export default DsMedicalPage;
